import React from 'react';

import { QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { fileSizeZeroCopy } from 'design-system/src/sharedCopy';
import { CookieColumnName, TasksFilterValues, UtmParams, SUPPORTED_EHR_NAMES, type SupportedEhrName } from './types';
import { PharmacyAddress } from './api';
import { MULTI_SELECT_DELINEATOR } from './constants';
import { DropdownOption, NameObject } from 'shared-utils/types/dropdownFilter';
import { getCookie, createCookie, deleteCookie } from 'design-system/src/cookieUtils';
import type { MultiselectAutocompleteOption } from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { serverErrorCopy } from 'design-system/src/sharedCopy';
import { UserDefaultFilters, User } from './auth';
import type { Ehr } from './types';
import { pluralize } from 'shared-utils/utils/formatString';

export const requiredErrorString = 'Required';
export const requiredErrorMessage = <ErrorMessage>{requiredErrorString}</ErrorMessage>;

/**
 * Strips HTML tags from a string.
 * Useful for displaying plain text versions of HTML content (e.g., in search results or previews).
 */
export const stripHtmlTags = (text: string): string => text.replace(/<[^>]*>/g, '');

export const extractUtmParams = (searchParams: URLSearchParams): UtmParams => {
  const utmKeys: (keyof UtmParams)[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  return utmKeys.reduce<UtmParams>((acc, key) => {
    const value = searchParams.get(key);
    if (value) {
      acc[key] = value.trim();
    }
    return acc;
  }, {});
};

// File validation constants
// restrict image file types to only the ones that are supported by retool
// https://docs.retool.com/apps/guides/presentation-styling/images#display-images-from-retool-storage
export const acceptedMimeTypes = ['image/bmp', 'image/gif', 'image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
// Generate acceptedFileTypes string from the MIME types array to keep them in sync
export const acceptedFileTypes = acceptedMimeTypes.join(', ');
// Generate user-friendly file extensions for error messages
export const acceptedFileExtensions = acceptedMimeTypes.map((mimeType) => '.' + mimeType.split('/')[1]).join(', ');

const maxFileSizeInMB = 15;
export const maxFileSizeInBytes = maxFileSizeInMB * 1024 * 1024;

/**
 * Format file size in bytes to human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validates files for size and MIME type requirements.
 * @param files - Array of File objects or NewCommentFile objects to validate
 * @returns null if all files are valid, or an error message string if validation fails
 */
export const validateFiles = (files: File[] | { file: File }[]): string | null => {
  // Extract File objects if we received NewCommentFile objects
  const fileObjects = files.map((item) => ('file' in item ? item.file : item));

  // Check file size
  if (fileObjects.some((file) => file.size > maxFileSizeInBytes)) {
    return 'File size exceeds 15MB. Please upload a smaller file.';
  }

  // Check MIME types
  const invalidFiles = fileObjects.filter((file) => {
    return !acceptedMimeTypes.includes(file.type);
  });

  if (invalidFiles.length > 0) {
    return `Please upload files with supported extensions: ${acceptedFileExtensions}`;
  }

  return null;
};

// TODO(adalman): Update this to use the shared-utils/utils/updateUrlSearchParams.ts file.
export const updateUrlSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void,
  keyValues: Record<string, string | undefined>,
  deletePageParam: boolean = false,
) => {
  const newParams = new URLSearchParams(searchParams);
  Object.entries(keyValues).forEach(([key, value]) => {
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
  });
  if (deletePageParam) {
    newParams.delete('page');
  }
  setSearchParams(newParams);
};

export const getMultiSelectValuesFromSearchParams = (searchParams: URLSearchParams, key: string): string[] => {
  const param = searchParams.get(key);
  return param ? param.split(MULTI_SELECT_DELINEATOR).filter((v) => v !== '') : [];
};

/**
 * The following ___FilterValuesFromSearchParams functions are only used on one table each, but they're
 * grouped here to keep the logic consistent.
 */
export const tasksFilterValuesFromSearchParams = (searchParams: URLSearchParams): TasksFilterValues => {
  return {
    office: getMultiSelectValuesFromSearchParams(searchParams, 'office'),
    prescriber: getMultiSelectValuesFromSearchParams(searchParams, 'prescriber'),
    type: getMultiSelectValuesFromSearchParams(searchParams, 'type'),
    drug_name: getMultiSelectValuesFromSearchParams(searchParams, 'drug_name'),
    drug_category: getMultiSelectValuesFromSearchParams(searchParams, 'drug_category'),
    startDate: searchParams.get('last_comment_created_at_start') || '',
    endDate: searchParams.get('last_comment_created_at_end') || '',
  };
};

export const priorAuthReviewsFilterValuesFromSearchParams = (searchParams: URLSearchParams) => {
  return {
    office: getMultiSelectValuesFromSearchParams(searchParams, 'office'),
    prescriber: getMultiSelectValuesFromSearchParams(searchParams, 'prescriber'),
    drug_name: getMultiSelectValuesFromSearchParams(searchParams, 'drug_name'),
  };
};

export const expiringPriorAuthFilterValuesFromSearchParams = (searchParams: URLSearchParams) => {
  return {
    office: getMultiSelectValuesFromSearchParams(searchParams, 'office'),
    prescriber: getMultiSelectValuesFromSearchParams(searchParams, 'prescriber'),
    drug_name: getMultiSelectValuesFromSearchParams(searchParams, 'drug_name'),
  };
};

export const notificationsFilterValuesFromSearchParams = (searchParams: URLSearchParams) => {
  return {
    office: getMultiSelectValuesFromSearchParams(searchParams, 'office'),
    prescriber: getMultiSelectValuesFromSearchParams(searchParams, 'prescriber'),
    drug_name: getMultiSelectValuesFromSearchParams(searchParams, 'drug_name'),
    type: getMultiSelectValuesFromSearchParams(searchParams, 'type'),
  };
};

// Format phone number as user types
export const formatPhoneNumberIncrementally = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) {
    return '';
  } else if (digits.length <= 3) {
    return `(${digits}`;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

export const phoneNumberUnformatted = (phoneNumber: string) => {
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Format a phone number with +1 country code for US numbers (E.164 format).
 * If the phone number has exactly 10 digits, it's assumed to be a US number and prefixed with +1.
 * Otherwise, the original value is returned.
 *
 * @param phoneNumber - Phone number string (may include formatting like "(555) 123-4567")
 * @returns Phone number in E.164 format (+1 for US numbers) or original value, or undefined if no phone number
 *
 * @example
 * formatPhoneNumberWithCountryCode('(555) 123-4567') // '+15551234567'
 * formatPhoneNumberWithCountryCode('5551234567') // '+15551234567'
 * formatPhoneNumberWithCountryCode('+44 20 1234 5678') // '+44 20 1234 5678'
 * formatPhoneNumberWithCountryCode('') // undefined
 */
export const formatPhoneNumberWithCountryCode = (phoneNumber?: string): string | undefined => {
  if (!phoneNumber) return undefined;

  const phoneDigits = phoneNumberUnformatted(phoneNumber);
  return phoneDigits.length === 10 ? `+1${phoneDigits}` : phoneNumber;
};

export function convertBooleanToYesNo(value: boolean | null) {
  return value === true ? 'Yes' : value === false ? 'No' : null;
}

export function convertYesNoToBoolean(value: 'Yes' | 'No' | null) {
  return value === 'Yes' ? true : value === 'No' ? false : null;
}

/**
 * Office names could be addresses, so we want to make sure anything that's an address is placed after actual office names
 * An address starting with a number is our approximation of identifying an address
 */
export const sortOfficeNames = (officeNameA: string, officeNameB: string): number => {
  const startsWithNumber = (str: string) => /\d/.test(str[0]);

  // If `a` starts with a number and `b` does not, `a` goes after `b`
  if (startsWithNumber(officeNameA) && !startsWithNumber(officeNameB)) return 1;

  // If `b` starts with a number and `a` does not, `b` goes after `a`
  if (!startsWithNumber(officeNameA) && startsWithNumber(officeNameB)) return -1;

  // Otherwise, sort alphabetically
  return officeNameA.localeCompare(officeNameB);
};

/**
 * Format the remaining time for display
 */
export const formatRemainingTime = (expiredAt: string, prefix: string = 'PA auto-submits in'): string => {
  const now = new Date();
  const expirationDate = new Date(expiredAt);
  const diffTime = expirationDate.getTime() - now.getTime();
  const remainingDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));

  if (remainingDays === 0) {
    const remainingHours = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60)));
    if (remainingHours < 1) {
      return `${prefix} <1 ${pluralize(1, 'hour', 'hours')}`;
    }
    return `${prefix} ${remainingHours} ${pluralize(remainingHours, 'hour', 'hours')}`;
  }
  return `${prefix} ${remainingDays} ${pluralize(remainingDays, 'day', 'days')}`;
};

/**
 * Format the remaining time for display in text (without prefix)
 */
export const formatRemainingTimeText = (expiredAt: string): string => {
  return formatRemainingTime(expiredAt, '');
};

/**
 * Determines if the expiration countdown should be shown for a task
 */
export const shouldShowExpirationCountdown = (
  taskKey: string,
  expirationMetadata: { is_held: boolean; expired_at: string } | null | undefined,
  taskStatus: string,
): boolean => {
  return (
    taskKey === 'pa_information_request_unmet_criteria' &&
    Boolean(expirationMetadata) &&
    !expirationMetadata?.is_held &&
    taskStatus !== 'completed'
  );
};

export const truncateLongText = (
  text: string,
  maxNumLength: number | undefined = 14,
  numCharsOnEnd: number | undefined = 4,
  includeTitle: boolean = true,
) => {
  if (text.length > maxNumLength) {
    const truncatedText = `${text.slice(0, maxNumLength - numCharsOnEnd)}...${numCharsOnEnd > 0 ? text.slice(-numCharsOnEnd) : ''}`;
    return includeTitle ? <span title={text}>{truncatedText}</span> : truncatedText;
  }
  return text;
};

export const hasValidFileSizes = (files: File[], action: 'selected' | 'submit'): boolean => {
  let allValid = true;
  files.forEach((file) => {
    if (file.size === 0) {
      const message = action === 'selected' ? '0kb file selected' : '0kb file detected on submit';
      console.error(message, { fileType: file.type });
      Sentry.captureMessage(message, 'error');
      allValid = false;
    } else {
      const infoMessage = action === 'selected' ? 'file upload info' : 'submitting file';
      // logging the file size for debugging in production. some files are being uploaded to s3 with 0kb
      // so we want to track the file size for a given session and view it in fullstory
      // eslint-disable-next-line no-console -- see comment above
      console.info(infoMessage, {
        size: file.size,
        sizeFormatted: formatFileSize(file.size),
        type: file.type,
      });
    }
  });

  // Show toast once at the end if any files were invalid
  if (!allValid) {
    toast.custom(() => <Toast variant="error">{fileSizeZeroCopy}</Toast>);
  }

  return allValid;
};

export const titleCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formattedName = (name: NameObject) => {
  return `${titleCase(name.first_name)} ${titleCase(name.last_name)}`;
};

export const parseNamesFromDropdownValue = (optionValue: string) => {
  if (!optionValue) {
    return null;
  }
  const [first_name, last_name] = optionValue.split('+');
  return { first_name, last_name };
};

export const constructTableFilterCookieKey = (memberId: string | null, columnName: CookieColumnName) => {
  return `${memberId}_${columnName}`;
};

export const getTableFilterValuesFromCookie = (memberId: string | null, columnName: CookieColumnName) => {
  const cookieKey = constructTableFilterCookieKey(memberId, columnName);

  try {
    return JSON.parse(getCookie(cookieKey) || '[]');
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, {
      extra: {
        cookieKey,
        cookieValue: getCookie(cookieKey),
      },
    });
    return [];
  }
};

// this method is now only used for the RxTable office filters because that's the only filter
// that uses both search params and cookies
export const getDropdownInitialValueFromSearchParamsAndCookie = (
  fullUrlParamsString: string = '',
  memberId: string | null,
  columnName: CookieColumnName,
  // the optionsList is used to get the user-friendly description for the option from
  // the value, if needed
  optionsList?: DropdownOption[],
): DropdownOption[] => {
  // url params take precedence over cookie values, so we only get the cookie values if there are no url params to use
  if (!fullUrlParamsString) {
    return memberId && columnName ? getTableFilterValuesFromCookie(memberId, columnName) : [];
  }

  return fullUrlParamsString.split(MULTI_SELECT_DELINEATOR).map((substring) => {
    if (optionsList) {
      const descriptionFromValue = substring.replace('+', ' ');
      const descriptionFromOptionsList = optionsList.find((option) => option.value === substring)?.description;
      return {
        value: substring,
        description: descriptionFromOptionsList || descriptionFromValue,
      };
    }
    return {
      value: substring,
      description: substring.replace('+', ' '),
    };
  });
};

export const getDropdownInitialValueFromSearchParams = (
  userDefaultFilters: UserDefaultFilters | null | undefined,
  fullUrlParamsString: string = '',
  columnName?: 'offices' | 'providers' | 'drugs' | 'drug_category',
  // the optionsList is used to get the user-friendly description for the option from
  // the value, if needed
  optionsList?: DropdownOption[],
): DropdownOption[] => {
  // url params take precedence over default filters values, so we only get the default filters values if there are no url params to use
  if (!fullUrlParamsString) {
    if (columnName && userDefaultFilters) {
      const defaultFilterItems = userDefaultFilters[columnName as keyof UserDefaultFilters] || [];
      return defaultFilterItems.map((item) => ({
        value: ('id' in item ? item.id : item.npi) as string,
        description: item.name,
      }));
    } else {
      return [];
    }
  }

  return fullUrlParamsString.split(MULTI_SELECT_DELINEATOR).map((substring) => {
    if (optionsList) {
      const descriptionFromValue = substring.replace('+', ' ');
      const descriptionFromOptionsList = optionsList.find((option) => option.value === substring)?.description;
      return {
        value: substring,
        description: descriptionFromOptionsList || descriptionFromValue,
      };
    }
    return {
      value: substring,
      description: substring.replace('+', ' '),
    };
  });
};

// This is passed to the MultiselectAutocomplete component. Do not pass this to the
// table column filters; the results are in a different format than the column filters need.
export const getInitialMultiselectFilterValueFromCookie = (memberId: string | null, columnName: CookieColumnName) => {
  return getTableFilterValuesFromCookie(memberId, columnName);
};

// This is passed to the table column filter for frontend table filtering. Do not pass this to the
// MultiselectAutocomplete component; the results are in a different format than the MultiselectAutocomplete needs.
export const getInitialTableColumnFilterValueFromCookie = (memberId: string | null, columnName: CookieColumnName) => {
  return getTableFilterValuesFromCookie(memberId, columnName).map(
    (option: MultiselectAutocompleteOption) => option.value,
  );
};

export const updateTableColumnFilterValueCookie = (
  value: MultiselectAutocompleteOption[],
  memberId: string | null,
  columnName: CookieColumnName,
) => {
  const cookieKey = constructTableFilterCookieKey(memberId, columnName);

  if (!value.length) {
    deleteCookie(cookieKey);
  } else {
    createCookie(cookieKey, JSON.stringify(value));
  }
};

export const getAddressString = (address: PharmacyAddress) => {
  return [address.name, address.line1, address.line2, `${address.city}, ${address.state} ${address.postal_code}`]
    .filter(Boolean)
    .join(', ');
};

export const formatName = (name: string) => {
  const parts = name.toLowerCase().split(' ');
  if (parts.length <= 2) {
    return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
  const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const last = parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1);
  const middle = parts
    .slice(1, -1)
    .map((part) => part.charAt(0).toUpperCase().concat('.'))
    .join(' ');
  return `${first} ${middle} ${last}`;
};

/**
 * Replaces text on the page with new text.
 * @param searchText - The text to search for.
 * @param replaceText - The text to replace the search text with.
 * @param querySelector - The CSS selector to search for. Defaults to all elements.
 */
export const replaceTextOnPage = (searchText: string, replaceText: string, querySelector: string = '*') => {
  const elements = document.querySelectorAll(querySelector);

  elements.forEach((element) => {
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.includes(searchText)) {
        node.nodeValue = node.nodeValue.replaceAll(searchText, replaceText);
      }
    });
  });
};

export const areStringArraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

export function applySavedFilters(
  authContext: {
    user: User | null;
    setUser: (user: User | null) => void;
  },
  providersWithNames: { npi: string; name: string }[],
  queryClient?: QueryClient,
) {
  if (!authContext.user) {
    return;
  }

  // Update the user context with the new filter data so the user sees
  // the effects of the filter change immediately
  authContext.setUser({
    ...authContext.user,
    default_filters: { providers: providersWithNames },
  });

  // Refetch notifications query to update red dots in sidebar
  if (queryClient) {
    queryClient.refetchQueries({ queryKey: ['notifications'] });
  }
}

/**
 * Formats address in standard format: "Address, City, State Zip"
 * Returns empty string if no address components are provided.
 */
export const formatAddress = (
  address?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
): string => {
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const cityStateZip = [city, stateZip].filter(Boolean).join(', ');
  return [address, cityStateZip].filter(Boolean).join(', ');
};

export const convertToAcronym = (str: string) => {
  return str
    .split(' ')
    .filter((word) => word)
    .map((word) => word[0].toUpperCase())
    .join('');
};

/**
 * Helper function to get prescriber filter values from URL parameters and default filters.
 * Converts NPIs from URL to display names, or falls back to default prescriber names.
 *
 * @param filterValues - Filter values extracted from URL search params
 * @param filterOptions - Portal table filter options containing prescriber data
 * @param defaultPrescriberNames - Default prescriber names from user preferences
 * @param hasUserClearedFilters - Ref indicating if user has explicitly cleared filters
 * @returns Array of prescriber display names for the filter
 */
export const getPrescriberFilterValue = (
  filterValues: { prescriber: string[] },
  filterOptions: { prescribers?: Array<{ value: string; description: string }> } | undefined,
  defaultPrescriberNames: string[] | undefined,
  hasUserClearedFilters: React.MutableRefObject<boolean>,
): string[] => {
  // Handle prescriber filter values: convert URL NPIs to names, or use defaults if no URL params and user hasn't cleared
  let prescriberValue: string[] = [];

  // Note: prescriber URL values are only applied once options load; if options fail to load,
  // the prescriber filter will remain empty even if `prescriber` is present in searchParams.
  if (filterValues.prescriber.length && filterOptions?.prescribers) {
    // Convert prescriber NPIs from URL to display names
    prescriberValue = filterValues.prescriber
      .map((npi) => filterOptions.prescribers!.find((p) => p.value === npi)?.description)
      .filter((name): name is string => !!name);
  } else if (!filterValues.prescriber.length && !hasUserClearedFilters.current) {
    // Use default prescriber names when no prescriber params in URL and user hasn't explicitly cleared filters
    prescriberValue = defaultPrescriberNames || [];
  }

  return prescriberValue;
};

export const rateLimitErrorCopy = "We weren't able to process your request at this time. Please try again later.";

export const handleServerError = (error: any): string => {
  Sentry.captureException(error, {
    extra: {
      status: error?.response?.status,
      detail: error?.response?.data?.detail,
      url: error?.config?.url,
      method: error?.config?.method,
    },
  });

  // Handle rate limiting (429 errors) with specific message
  if (error?.response?.status === 429) {
    return rateLimitErrorCopy;
  }

  // Handle other API errors with their detail or fallback to server error
  return error?.response?.data?.detail || serverErrorCopy;
};

/************************************************************
 * EHR UTILS
 ************************************************************/

export const isValidEhr = (value: unknown): value is SupportedEhrName => {
  return typeof value === 'string' && SUPPORTED_EHR_NAMES.includes(value as SupportedEhrName);
};

// EHR type to display name mapping for supported integrations
export const EHR_DISPLAY_NAME_MAP: Record<SupportedEhrName, string> = {
  ema_modmed: 'EMA',
  drchrono: 'DrChrono',
  elation: 'Elation',
  eclinicalworks: 'eClinicalWorks',
  next_gen: 'NextGen',
  ggastro_modmed: 'gGastro',
  athena: 'Athena',
  veradigm: 'Veradigm',
};

/**
 * Gets the display name for a valid EHR type.
 * @param ehr - The EHR type
 * @returns The display name for the EHR if it's a valid/supported EHR, otherwise returns the EHR value as-is
 */
export const getEhrDisplayName = (ehr: Ehr): string => {
  return isValidEhr(ehr) ? EHR_DISPLAY_NAME_MAP[ehr] : ehr;
};

/**
 * Returns a required field rule for react-hook-form.
 * @param fieldName - The name of the field (e.g., "First name", "Email")
 * @returns A required rule object with value: true and a "{fieldName} is required" message
 */
export const requiredFieldRule = (fieldName: string) => ({
  value: true as const,
  message: `${fieldName} is required`,
});

export const enterPlaceholder = (fieldName: string) => `Enter ${fieldName.toLowerCase()}`;

/**
 * Formats a TIN (Tax Identification Number) string as the user types,
 * applying the XXX-XX-XXXX pattern incrementally.
 */
export const formatTin = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export const selectPlaceholder = (fieldName: string) => `Select ${fieldName.toLowerCase()}`;

/**
 * Validates that a field has content and contains non-whitespace characters
 * @param fieldName - The name of the field for error messages (e.g., "First name", "Last name"), used in error message
 * @returns A validation function for react-hook-form
 */
export const validateNonWhitespaceField = (fieldName: string) => {
  return (value: string | undefined): true | string => {
    if (!value || typeof value !== 'string') {
      return `${fieldName} is required`;
    }
    if (value.trim().length === 0) {
      return `${fieldName} must contain non-whitespace characters`;
    }
    return true;
  };
};
